import { Component, Inject, OnInit, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DecimalPipe } from '@angular/common';
import { CreateCampaignApiService } from '../../../services/create-campaign-api.service';
import { SelectGroupResult } from '../../../model/get-groups-by-params.model';

export interface SelectGroupDialogData {
  application_id: string;
  region_id: string;
}

@Component({
    selector: 'select-group-dialog',
    templateUrl: 'select-group-dialog.component.html',
    styleUrls: ['select-group-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DecimalPipe,
        MatDialogModule,
        MatButtonModule,
        MatListModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
    ]
})
export class SelectGroupDialogComponent implements OnInit {
  groups: SelectGroupResult[] = [];
  filteredGroups = signal<SelectGroupResult[]>([]);
  selectedGroups = signal<SelectGroupResult[]>([]);
  loading = signal(true);

  private dialogRef = inject(MatDialogRef<SelectGroupDialogComponent>);
  private apiService = inject(CreateCampaignApiService);
  data: SelectGroupDialogData = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    const params = {
      application_ids: [this.data.application_id],
      region_ids: [this.data.region_id],
      label_ids: [],
      group_name: '',
      page: 0,
      page_size: 100,
    };

    this.apiService.getGroupsByParams(params).subscribe({
      next: (response) => {
        this.groups = response.items.sort((a, b) => (b.member_count ?? 0) - (a.member_count ?? 0));
        this.filteredGroups.set(this.groups);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  onSearch(event: Event): void {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (!query) {
      this.filteredGroups.set(this.groups);
      return;
    }
    this.filteredGroups.set(this.groups.filter(g =>
      g.group_name?.toLowerCase().includes(query) || g.group_user?.toLowerCase().includes(query)
    ));
  }

  onSelectionChange(event: MatSelectionListChange): void {
    const option = event.options[0];
    const group = option.value as SelectGroupResult;
    const current = this.selectedGroups();

    if (option.selected) {
      this.selectedGroups.set([...current, group]);
    } else {
      this.selectedGroups.set(current.filter(g => g.id !== group.id));
    }
  }

  isSelected(group: SelectGroupResult): boolean {
    return this.selectedGroups().some(g => g.id === group.id);
  }

  onConfirm(): void {
    this.dialogRef.close({ result: 'okay', groups: this.selectedGroups() });
  }

  onCancel(): void {
    this.dialogRef.close({ result: 'cancel' });
  }
}
